import React, { useEffect, useMemo, useRef, useState } from "react";
import Modal from "../utils/Modal/Modal";
import {
  AdminActionButton,
  AdminActionsContainer,
  CategoryContainer,
  CategoryFadeWrapper,
  CategoryItem,
  CategoryText,
  Content,
  Header,
  IconWrapper,
  ModalContainer,
  Tab,
  TabsContainer,
  TabText,
  Title,
  RightContent,
  RightContentOverlay,
  StyledVideo,
  StyledImage,
  Container,
  GenerateContainer,
  InfoIconWrapper,
  TooltipContainer,
  TooltipText,
  GenerateContainerMobile,
  StyledIconWrapper,
  RowIcons,
} from "./GenerateCollectionModal.styles";

import { ReactComponent as CloseIcon } from "../../assets/icons/close.svg";
import { ReactComponent as PlaylistIcon } from "../../assets/icons/bookmark.svg";
import { ReactComponent as CollectionDetailsIcon } from "../../assets/icons/collectionDetails.svg";
import { ReactComponent as ImageIcon } from "../../assets/icons/image.svg";
import { ReactComponent as VideoRecorderIcon } from "../../assets/icons/video-recorder.svg";
import { ReactComponent as InfoIcon } from "../../assets/icons/info-circle.svg";
import { useDispatch, useSelector } from "react-redux";
import { selectUser } from "../../store/slices/user/slice";
import {
  selectGenerateCollection,
  selectHideCollectionPending,
  selectHideCollectionVipPending,
  selectSelectedCollection,
  selectSelectedTrainModel,
  selectToggleToFavoriteLoading,
} from "../../store/slices/model/slice";
import {
  generateCollection,
  hideCollection,
  hideCollectionVip,
  toggleToFavorite,
} from "../../store/slices/model/asyncThunks";
import ExtendVideo from "../ExtendVideo/ExtendVideo";
import ImageToVideo from "../ImageToVideo/ImageToVideo";
import { useLockBodyScroll } from "../../hooks/useLockBodyScroll";
import { useScrollFade } from "../../hooks/useScrollFade";
import { formatModelName, isImageOutput } from "../../utils/utils";
import {
  MEDIA_TYPE,
  NUDIFY_PRICE,
  VIDEO_DURATION_PRICE,
  VIDEO_FPS_PRICE,
  VIDEO_RESOLUTION_PRICE,
  DEFAULT_VIDEO_DURATION,
  DEFAULT_VIDEO_FPS,
  DEFAULT_VIDEO_RESOLUTION,
  ALL_VIDEO_RESOLUTIONS,
  ALLOWED_HIDE_COLLECTION_USER_IDS,
  UPSCALE_PRICE,
  AUDIO_PRICE,
} from "../../utils/constants";
import { useNavigate } from "react-router-dom";
import CropModal from "../CropModal/CropModal";
import AddToPlaylistModal from "../AddToPlaylistModal/AddToPlaylistModal";
import CreatePlaylistModal from "../CreatePlaylistModal/CreatePlaylistModal";
import EnterPromptModal from "../EnterPromptModal/EnterPromptModal";
import ConfirmationModal from "../ConfirmationModal/ConfirmationModal";
import LottieIcon from "../LottieIcon/LottieIcon";
import likeAnimation from "../../assets/animations/like.json";
import settingsAnimation from "../../assets/animations/settings.json";
import GradientStarIcon from "../GradientStarIcon/GradientStarIcon";
import { toast } from "sonner";
import { useMediaQuery } from "react-responsive";
import FlyToCollection from "../FlyToCollection/FlyToCollection";
import {
  selectVideoSettings,
  triggerCollectionTabAnimation,
} from "../../store/slices/common/slice";
import ExplosionButton from "../ExplosionButton/ExplosionButton";
import GenerationToast from "../utils/GenerationToast";
import UserGalleryModal from "../UserGalleryModal/UserGalleryModal";
import CreateImageModal from "../CreateImageModal/CreateImageModal";

// import TermsCheckboxComponent from "../TermsCheckboxComponent/TermsCheckboxComponent";

const MODAL_CONTENT = {
  IMAGE_TO_VIDEO: "IMAGE_TO_VIDEO",
  EXTEND_VIDEO: "EXTEND_VIDEO",
};

const hasVipTag = (collection) =>
  Array.isArray(collection?.tags) &&
  collection.tags.some((tag) =>
    typeof tag === "string" ? tag === "VIP" : tag?.name === "VIP",
  );

const pause = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const getSupportedVideoResolution = (resolution) =>
  ALL_VIDEO_RESOLUTIONS.includes(resolution)
    ? resolution
    : DEFAULT_VIDEO_RESOLUTION;

const hasPrice = (priceMap, value) =>
  Object.prototype.hasOwnProperty.call(priceMap, value);

const getSupportedVideoDuration = (duration) => {
  const normalizedDuration = Number(duration);

  return hasPrice(VIDEO_DURATION_PRICE, normalizedDuration)
    ? normalizedDuration
    : DEFAULT_VIDEO_DURATION;
};

const getSupportedVideoFps = (fps) => {
  const normalizedFps = Number(fps);

  return hasPrice(VIDEO_FPS_PRICE, normalizedFps)
    ? normalizedFps
    : DEFAULT_VIDEO_FPS;
};

const MemoizedRightContent = React.memo(({ output, input }) => {
  if (!output || !input) return null;

  return (
    <RightContent>
      {output.type === MEDIA_TYPE.VIDEO ? (
        <StyledVideo
          autoplay
          src={output.video.compressedUrl || output.video.url}
          imageSrc={input.image.resizedImage.url || input.image.url}
        />
      ) : (
        <StyledImage src={output.image.url} />
      )}
      <RightContentOverlay />
    </RightContent>
  );
});

const GenerateCollectionModal = ({
  isOpen,
  onClose,
  playlistId,
  fromTrainingSelectCoverPage,
  profilePage,
  profileUsername,
}) => {
  useLockBodyScroll(isOpen);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const user = useSelector(selectUser);
  const collection = useSelector(selectSelectedCollection);

  const isOnCollectionUserProfile =
    profilePage &&
    !!collection?.username &&
    (profileUsername === collection.username ||
      user?.username === collection?.username);

  const selectedTrainModel = useSelector(selectSelectedTrainModel);
  const trainModelFromPage = fromTrainingSelectCoverPage
    ? selectedTrainModel?.data?.collections?.[0]?.trainModel
    : undefined;
  const loading = useSelector(selectToggleToFavoriteLoading);
  const { loading: generateLoading } = useSelector(selectGenerateCollection);
  const hideCollectionPending = useSelector(selectHideCollectionPending);
  const hideCollectionVipPending = useSelector(selectHideCollectionVipPending);
  const videoSettings = useSelector(selectVideoSettings);
  const isMobile = useMediaQuery({ maxWidth: 768 });
  const [categoryRef, isCategoryFaded] = useScrollFade("horizontal", [
    collection?.keywords,
  ]);
  const tooltipRef = useRef(null);
  const uploadedImageRef = useRef(null);
  const uploadedVideoRef = useRef(null);

  const isLoading =
    loading ||
    generateLoading ||
    hideCollectionPending ||
    hideCollectionVipPending;
  const userId = user?._id || user?.id;
  const canManageCollectionVisibility =
    !!userId && ALLOWED_HIDE_COLLECTION_USER_IDS.includes(userId);

  const [selectedTab, setSelectedTab] = useState(MODAL_CONTENT.IMAGE_TO_VIDEO);
  const [uploadedImage, setUploadedImage] = useState(null);
  const [uploadedVideo, setUploadedVideo] = useState(null);
  const [nudifyEnabled, setNudifyEnabled] = useState(false);
  const [upscaleEnabled, setUpscaleEnabled] = useState(false);
  const [audioEnabled, setAudioEnabled] = useState(
    localStorage.getItem("audioEnabled") === "true",
  );

  // const [shouldShake, setShouldShake] = useState(false);
  // const [showOutline, setShowOutline] = useState(false);
  // const [agreeTos, setAgreeTos] = useState(false);
  const [customPrompt, setCustomPrompt] = useState("");
  const [negativePrompt, setNegativePrompt] = useState("");
  const [audioPrompt, setAudioPrompt] = useState("");
  const [showPrompts, setShowPrompts] = useState(false);

  const [customSeed, setCustomSeed] = useState(
    Math.floor(Math.random() * 10000000) + 1,
  );
  const [showTooltip, setShowTooltip] = useState(false);
  const [isExploding, setIsExploding] = useState(false);
  const [isGeneratingFromGallery, setIsGeneratingFromGallery] = useState(false);
  const [isCollectionHiddenLocally, setIsCollectionHiddenLocally] =
    useState(false);
  const [isVipTagHiddenLocally, setIsVipTagHiddenLocally] = useState(false);
  const [confirmationAction, setConfirmationAction] = useState(null);

  const [isOpenCropModal, setIsOpenCropModal] = useState(false);
  const [isUserGalleryModalOpen, setIsUserGalleryModalOpen] = useState(false);
  const [isCreateImageFromGalleryOpen, setIsCreateImageFromGalleryOpen] =
    useState(false);
  const [isAddToPlaylistModalOpen, setIsAddToPlaylistModalOpen] =
    useState(false);
  const [isCreatingNew, setIsCreatingNew] = useState(false);
  const [isCreatePlaylistModalOpen, setIsCreatePlaylistModalOpen] =
    useState(false);
  const [isEnterPromptModalOpen, setIsEnterPromptModalOpen] = useState(false);
  const [playlistName, setPlaylistName] = useState("");

  const [flyAnimation, setFlyAnimation] = useState({
    isActive: false,
    imageUrl: null,
    startPosition: null,
    imageDimensions: null,
    onComplete: null,
  });

  const [videoResolution, setVideoResolution] = useState(
    getSupportedVideoResolution(localStorage.getItem("videoResolution")),
  );
  const [fps, setFps] = useState(
    getSupportedVideoFps(localStorage.getItem("videoFps")),
  );
  const [videoDuration, setVideoDuration] = useState(
    getSupportedVideoDuration(localStorage.getItem("videoDuration")),
  );

  const collectionPrice = useMemo(() => {
    if (!collection) return 0;

    const basePrice = Number(collection?.price ?? 0);
    const resolutionPrice =
      VIDEO_RESOLUTION_PRICE[videoResolution] ??
      VIDEO_RESOLUTION_PRICE[DEFAULT_VIDEO_RESOLUTION] ??
      0;
    const durationPrice =
      VIDEO_DURATION_PRICE[videoDuration] ??
      VIDEO_DURATION_PRICE[DEFAULT_VIDEO_DURATION] ??
      0;
    const fpsPrice =
      VIDEO_FPS_PRICE[fps] ?? VIDEO_FPS_PRICE[DEFAULT_VIDEO_FPS] ?? 0;

    if (selectedTab === MODAL_CONTENT.IMAGE_TO_VIDEO) {
      return !isImageOutput(collection.modelType)
        ? basePrice +
            (nudifyEnabled ? NUDIFY_PRICE : 0) +
            (upscaleEnabled ? UPSCALE_PRICE : 0) +
            (audioEnabled ? AUDIO_PRICE : 0) +
            resolutionPrice +
            durationPrice +
            fpsPrice
        : basePrice;
    } else {
      return (
        basePrice +
        (audioEnabled ? AUDIO_PRICE : 0) +
        resolutionPrice +
        durationPrice +
        fpsPrice
      );
    }
  }, [
    collection,
    selectedTab,
    nudifyEnabled,
    upscaleEnabled,
    videoResolution,
    audioEnabled,
    videoDuration,
    fps,
  ]);

  const userGallerySelectedImages = useMemo(() => {
    if (selectedTab === MODAL_CONTENT.EXTEND_VIDEO) {
      return uploadedVideo?.url ? [uploadedVideo.url] : [];
    }

    return uploadedImage?.url ? [uploadedImage.url] : [];
  }, [selectedTab, uploadedImage?.url, uploadedVideo?.url]);

  const handleFavoriteToggle = () => {
    if (!isLoading && collection._id) {
      dispatch(toggleToFavorite({ collectionId: collection._id }));
    }
  };

  const handlePlaylistToggle = () => {
    if (!isLoading && collection._id) {
      setIsAddToPlaylistModalOpen(true);
    }
  };

  const hideCollectionAction = () => {
    if (!collection?._id || hideCollectionPending) {
      return;
    }

    const willShowCollection = isCollectionHidden;
    const hide = !willShowCollection;

    dispatch(hideCollection({ collectionId: collection._id, hide }))
      .unwrap()
      .then(() => {
        setIsCollectionHiddenLocally(!isCollectionHidden);
        setConfirmationAction(null);
        toast.success(
          willShowCollection
            ? "Collection shown successfully"
            : "Collection hidden successfully",
        );
      })
      .catch((err) => {
        toast.error(
          err ||
            (willShowCollection
              ? "Failed to show collection"
              : "Failed to hide collection"),
        );
      });
  };

  const hideCollectionVipAction = () => {
    if (!collection?._id || hideCollectionVipPending) {
      return;
    }

    const willShowVipTag = isVipTagHiddenLocally;
    const hide = !willShowVipTag;

    dispatch(hideCollectionVip({ collectionId: collection._id, hide }))
      .unwrap()
      .then(() => {
        setIsVipTagHiddenLocally(!isVipTagHiddenLocally);
        setConfirmationAction(null);
        toast.success(
          willShowVipTag
            ? "VIP tag shown successfully"
            : "Collection hidden from VIP category successfully",
        );
      })
      .catch((err) => {
        toast.error(
          err ||
            (willShowVipTag
              ? "Failed to show VIP tag"
              : "Failed to hide collection from VIP"),
        );
      });
  };

  const handleConfirmVisibilityAction = () => {
    if (confirmationAction === "hide") {
      hideCollectionAction();
      return;
    }

    if (confirmationAction === "hideVip") {
      hideCollectionVipAction();
    }
  };

  // const handleCheckboxClick = () => {
  //   const newValue = !agreeTos;
  //   setAgreeTos(newValue);
  //   if (newValue) {
  //     setShowOutline(false);
  //   }
  // };

  const handleGenerateSuccess = ({ mediaUrl, mediaRef, successMessage }) => {
    toast.custom(() => (
      <GenerationToast
        successMessage={successMessage}
        label="View"
        onClick={() => {
          navigate("/collection");

          window.scrollTo({
            top: 0,
            left: 0,
            behavior: "instant",
          });
        }}
      />
    ));

    setIsExploding(true);

    setTimeout(() => {
      setIsExploding(false);

      if (mediaUrl && mediaRef?.current) {
        setFlyAnimation({
          isActive: true,
          imageUrl: mediaUrl,
          startPosition: mediaRef.current.getBoundingClientRect(),
          imageDimensions: {
            width: mediaRef.current.offsetWidth,
            height: mediaRef.current.offsetHeight,
          },
          onComplete: () => {
            setFlyAnimation({
              isActive: false,
              imageUrl: null,
              startPosition: null,
              imageDimensions: null,
              onComplete: null,
            });
          },
        });

        handlePostGenerate();
      } else {
        handlePostGenerate();
      }
    }, 500);
  };

  const onGenerateClick = () => {
    if ((localStorage.getItem("wasPromptModalOpen") ?? false) === false) {
      setIsEnterPromptModalOpen(true);
      localStorage.setItem("wasPromptModalOpen", true);
      return;
    }
    // if (!agreeTos) {
    //   setShouldShake(true);
    //   setShowOutline(true);
    //   setTimeout(() => setShouldShake(false), 400);
    //   return;
    // }

    // localStorage.setItem("tosAccepted", "true");

    if (selectedTab === MODAL_CONTENT.IMAGE_TO_VIDEO) {
      dispatch(
        generateCollection({
          image: uploadedImage?.blob
            ? {
                file: new File(
                  [uploadedImage.blob],
                  `cropped-${Date.now()}.jpg`,
                  { type: "image/jpeg" },
                ),
                url: uploadedImage.url,
              }
            : uploadedImage,
          customPrompt,
          audioCustomPrompt: audioPrompt,
          audio: audioEnabled,
          negativePrompt,
          nudify: nudifyEnabled,
          upscale: upscaleEnabled,
          modelId: collection.model,
          resolution: videoResolution,
          seed: customSeed,
          fps,
          videoDuration,
          playlistId,
          trainModel: trainModelFromPage,
        }),
      )
        .unwrap()
        .then(() => {
          handleGenerateSuccess({
            mediaUrl: uploadedImage?.url,
            mediaRef: uploadedImageRef,
            successMessage: "Generation started...",
          });
        })
        .catch((err) => {
          toast.error(err);
        });
    } else {
      dispatch(
        generateCollection({
          video: uploadedVideo,
          customPrompt,
          negativePrompt,
          audio: audioEnabled,
          audioCustomPrompt: audioPrompt,
          modelId: collection.model,
          resolution: videoResolution,
          seed: customSeed,
          fps,
          videoDuration,
          playlistId,
          trainModel: trainModelFromPage,
        }),
      )
        .unwrap()
        .then(() => {
          handleGenerateSuccess({
            mediaUrl: uploadedVideo?.url,
            mediaRef: uploadedVideoRef,
            successMessage: "Extending started...",
          });
        })
        .catch((err) => {
          toast.error(err);
        });
    }
  };

  useEffect(() => {
    if (!showTooltip) return;

    const handleClickOutside = (event) => {
      if (tooltipRef.current && !tooltipRef.current.contains(event.target)) {
        setShowTooltip(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("touchstart", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("touchstart", handleClickOutside);
    };
  }, [showTooltip]);

  const resetStates = () => {
    setUploadedImage(null);
    setUploadedVideo(null);
    setCustomPrompt("");
    setNegativePrompt("");
    setAudioPrompt("");
    setNudifyEnabled(false);
    setUpscaleEnabled(false);
    setAudioEnabled(localStorage.getItem("audioEnabled") === "true");
    setShowPrompts(false);
    // setShouldShake(false);
    // setShowOutline(false);
    // setAgreeTos(false);
    setIsEnterPromptModalOpen(false);
    setIsUserGalleryModalOpen(false);
  };

  const handlePostGenerate = () => {
    const alreadyGeneratedVideo = localStorage.getItem("alreadyGeneratedVideo");

    if (!alreadyGeneratedVideo) {
      localStorage.setItem("alreadyGeneratedVideo", true);
      navigate("/collection");

      window.scrollTo({
        top: 0,
        left: 0,
        behavior: "instant",
      });
    }

    resetStates();
    onClose();
  };

  useEffect(() => {
    setIsCollectionHiddenLocally(collection?.isPublic === false);
    setIsVipTagHiddenLocally(false);
  }, [collection?._id, collection?.isPublic]);

  useEffect(() => {
    if (!isOpen) return;
    const savedResolution = localStorage.getItem("videoResolution");
    setVideoResolution(getSupportedVideoResolution(savedResolution));
    setFps(getSupportedVideoFps(localStorage.getItem("videoFps")));
    setVideoDuration(
      getSupportedVideoDuration(localStorage.getItem("videoDuration")),
    );
    setAudioEnabled(localStorage.getItem("audioEnabled") === "true");
  }, [isOpen]);

  useEffect(() => {
    if (!videoSettings) return;

    if (videoSettings.videoResolution) {
      setVideoResolution(
        getSupportedVideoResolution(videoSettings.videoResolution),
      );
    }
    if (videoSettings.fps) {
      setFps(getSupportedVideoFps(videoSettings.fps));
    }
    if (videoSettings.videoDuration) {
      setVideoDuration(getSupportedVideoDuration(videoSettings.videoDuration));
    }
    if (videoSettings.audioEnabled != null) {
      setAudioEnabled(Boolean(videoSettings.audioEnabled));
    }
  }, [videoSettings]);

  useEffect(() => {
    if (!collection) {
      return;
    }

    if (collection.nudify) {
      setNudifyEnabled(true);
    }

    if (collection.upscale) {
      setUpscaleEnabled(true);
    }

    if (collection.audio) {
      setAudioEnabled(true);
    }

    if (collection?.input?.video?.url) {
      setSelectedTab(MODAL_CONTENT.EXTEND_VIDEO);
    } else {
      setSelectedTab(MODAL_CONTENT.IMAGE_TO_VIDEO);
    }
  }, [collection]);

  const renderGenerateContainer = () => {
    const tooltipText =
      selectedTab === MODAL_CONTENT.IMAGE_TO_VIDEO
        ? "The uploaded image will be used as the first frame of the generated video. For best results, match the original image. The AI follows basic physics - objects not in the input image are unlikely to appear in the generated video."
        : "Extending a video reuses its last frame as the starting point for the new generation, making the final video longer and seamless.";

    const buttonText =
      selectedTab === MODAL_CONTENT.IMAGE_TO_VIDEO ? "Generate" : "Extend";

    return (
      <>
        <StyledIconWrapper
          onClick={() => {
            setIsEnterPromptModalOpen(true);
            localStorage.setItem("wasPromptModalOpen", true);
          }}
        >
          <LottieIcon
            animationData={settingsAnimation}
            trigger="hover"
            strokeColor={"#fff"}
            size={28}
          />
        </StyledIconWrapper>
        <ExplosionButton
          style={{ margin: "0px 6px", flex: 1, width: "100%" }}
          disabled={generateLoading}
          onClick={onGenerateClick}
          isLoading={generateLoading}
          isExploding={isExploding}
          small
        >
          {generateLoading ? (
            <l-line-spinner size="16" stroke="1.25" speed="1.5" color="black" />
          ) : (
            // <l-pulsar
            //   size="24"
            //   stroke="2"
            //   speed="2.2"
            //   color="rgba(63, 136, 247, 1)"
            // />
            <>
              {buttonText}
              <GradientStarIcon />
              {Math.ceil(collectionPrice)}
            </>
          )}
        </ExplosionButton>
        <InfoIconWrapper
          ref={tooltipRef}
          $active={collection?.description && collection.description !== ""}
          onMouseEnter={() => {
            if (!isMobile) {
              setShowTooltip(true);
            }
          }}
          onMouseLeave={() => {
            if (!isMobile) {
              setShowTooltip(false);
            }
          }}
          onClick={() => {
            if (isMobile) {
              setShowTooltip(!showTooltip);
            }
          }}
        >
          <InfoIcon width={24} height={24} />
          {showTooltip && (
            <TooltipContainer>
              {selectedTab === MODAL_CONTENT.IMAGE_TO_VIDEO ? (
                <>
                  <TooltipText>
                    The uploaded image will be used as the first frame of the
                    generated video.
                  </TooltipText>
                  <TooltipText>
                    For best results, match the original image. The AI follows
                    basic physics - objects not in the input image are unlikely
                    to appear in the generated video.
                  </TooltipText>
                </>
              ) : (
                <TooltipText>{tooltipText}</TooltipText>
              )}

              {collection?.description && collection.description !== "" && (
                <>
                  <TooltipText $light>Tips</TooltipText>
                  <TooltipText>{collection.description}</TooltipText>
                </>
              )}
            </TooltipContainer>
          )}
        </InfoIconWrapper>
      </>
    );
  };

  if (!user || !collection) {
    return null;
  }

  const showHideVipTagButton =
    canManageCollectionVisibility &&
    (hasVipTag(collection) || isVipTagHiddenLocally);
  const isCollectionHidden =
    isCollectionHiddenLocally || collection?.isPublic === false;
  const confirmationTitle =
    confirmationAction === "hideVip"
      ? isVipTagHiddenLocally
        ? "Show VIP Tag"
        : "Hide VIP Tag"
      : isCollectionHidden
        ? "Show Collection"
        : "Hide Collection";
  const confirmationMessage =
    confirmationAction === "hideVip"
      ? isVipTagHiddenLocally
        ? "Are you sure you want to show this collection in the VIP category?"
        : "Are you sure you want to remove this collection from the VIP category?"
      : isCollectionHidden
        ? "Are you sure you want to show this collection?"
        : "Are you sure you want to hide this collection?";
  const confirmationLoading =
    confirmationAction === "hideVip"
      ? hideCollectionVipPending
      : hideCollectionPending;

  return (
    <>
      <UserGalleryModal
        isOpen={isUserGalleryModalOpen}
        onClose={() => setIsUserGalleryModalOpen(false)}
        onCreateImage={() => setIsCreateImageFromGalleryOpen(true)}
        onConfirm={async (mediaItems) => {
          if ((localStorage.getItem("wasPromptModalOpen") ?? false) === false) {
            setIsEnterPromptModalOpen(true);
            localStorage.setItem("wasPromptModalOpen", true);
            return false;
          }

          try {
            setIsGeneratingFromGallery(true);
            let successfulGenerations = 0;
            let firstSuccessfulMediaUrl = null;

            for (const [index, mediaItem] of mediaItems.entries()) {
              try {
                await dispatch(
                  generateCollection({
                    ...(mediaItem.type === "video"
                      ? { videoUrl: mediaItem.url }
                      : { imageUrl: mediaItem.url }),
                    customPrompt,
                    negativePrompt,
                    nudify: nudifyEnabled,
                    upscale: upscaleEnabled,
                    audio: audioEnabled,
                    audioCustomPrompt: audioPrompt,
                    modelId: collection.model,
                    resolution: videoResolution,
                    seed: customSeed,
                    fps,
                    videoDuration,
                    playlistId,
                    trainModel: trainModelFromPage,
                  }),
                ).unwrap();

                successfulGenerations += 1;

                if (!firstSuccessfulMediaUrl) {
                  firstSuccessfulMediaUrl = mediaItem.url;
                }
              } catch (error) {
                console.error("Gallery generation failed", error);
              }

              if (index < mediaItems.length - 1) {
                await pause(300);
              }
            }

            if (!successfulGenerations) {
              throw new Error("Failed to start generation");
            }

            if (successfulGenerations !== mediaItems.length) {
              toast.error("Failed to start one or more generations");
            }

            handleGenerateSuccess({
              mediaUrl:
                successfulGenerations === 1 ? firstSuccessfulMediaUrl : null,
              successMessage:
                successfulGenerations === 1
                  ? "Generation started..."
                  : `${successfulGenerations} generations started...`,
            });

            return true;
          } catch (error) {
            toast.error(error.message || "Failed to start generation");
            return false;
          } finally {
            setIsGeneratingFromGallery(false);
          }
        }}
        selectedImages={userGallerySelectedImages}
        uploadedImagesCount={0}
        maxSelectable={6}
        confirmLabel="Generate"
        generatePrice={collectionPrice}
        showPrice
        isConfirmLoading={isGeneratingFromGallery}
        overlayZIndex={3600}
        showUploadTile={selectedTab !== MODAL_CONTENT.EXTEND_VIDEO}
        allowedMediaTypes={
          selectedTab === MODAL_CONTENT.EXTEND_VIDEO
            ? ["video"]
            : ["image"]
        }
        onOpenSettings={() => {
          setIsEnterPromptModalOpen(true);
          localStorage.setItem("wasPromptModalOpen", true);
        }}
      />
      <CreateImageModal
        isOpen={isCreateImageFromGalleryOpen}
        onClose={() => setIsCreateImageFromGalleryOpen(false)}
        overlayZIndex={5000}
      />
      <CropModal
        selectedImage={uploadedImage}
        onClose={() => setIsOpenCropModal(false)}
        onConfirm={(croppedImageData) => {
          setUploadedImage({
            file: new File(
              [croppedImageData.blob],
              `cropped-${Date.now()}.jpg`,
              { type: "image/jpeg" },
            ),
            url: croppedImageData.url,
            blob: croppedImageData.blob,
          });
        }}
        isOpen={isOpenCropModal}
      />
      <Modal
        isOpen={isOpen}
        onClose={() => {
          if (!isLoading) {
            resetStates();
            onClose();
          }
        }}
      >
        <ModalContainer>
          <Header>
            <Title>{formatModelName(collection.name)}</Title>
            <RowIcons>
              <IconWrapper
                onClick={() => {
                  onClose();
                  navigate(`/template/${collection._id}`, {
                    state: { collection },
                  });

                  window.scrollTo({
                    top: 0,
                    left: 0,
                    behavior: "instant",
                  });
                }}
              >
                <CollectionDetailsIcon />
              </IconWrapper>

              {!collection?.isDisturbing && !collection?.isGay && (
                <IconWrapper onClick={handlePlaylistToggle}>
                  <PlaylistIcon />
                </IconWrapper>
              )}

              <IconWrapper style={{ zIndex: 1 }}>
                <LottieIcon
                  animationData={likeAnimation}
                  trigger="toggle"
                  isToggled={user.favoriteCollections.includes(collection._id)}
                  onToggle={handleFavoriteToggle}
                  likeSegment={[1, 90]}
                  unlikeSegment={[90, 180]}
                  size={50}
                  absolute
                />
              </IconWrapper>

              <IconWrapper
                onClick={() => {
                  if (!isLoading) {
                    resetStates();
                    onClose();
                  }
                }}
              >
                <CloseIcon />
              </IconWrapper>
            </RowIcons>
          </Header>

          <CategoryFadeWrapper $faded={isCategoryFaded}>
            <CategoryContainer ref={categoryRef}>
              <CategoryItem
                $click
                onClick={() => {
                  if (!isOnCollectionUserProfile) {
                    navigate(`/profile/${collection?.username}`);
                    window.scrollTo({
                      top: 0,
                      left: 0,
                      behavior: "instant",
                    });
                  }
                }}
              >
                <CategoryText>@{collection?.username}</CategoryText>
              </CategoryItem>

              {!!collection.keywords &&
                collection.keywords.map((keyword, index) => {
                  return (
                    <CategoryItem key={index}>
                      <CategoryText>{keyword}</CategoryText>
                    </CategoryItem>
                  );
                })}
            </CategoryContainer>
          </CategoryFadeWrapper>

          {canManageCollectionVisibility && (
            <AdminActionsContainer>
              <AdminActionButton
                onClick={() => setConfirmationAction("hide")}
                disabled={hideCollectionPending}
              >
                {hideCollectionPending
                  ? "Saving..."
                  : isCollectionHidden
                    ? "Show"
                    : "Hide"}
              </AdminActionButton>

              {showHideVipTagButton && (
                <AdminActionButton
                  onClick={() => setConfirmationAction("hideVip")}
                  disabled={hideCollectionVipPending}
                >
                  {hideCollectionVipPending
                    ? "Saving..."
                    : isVipTagHiddenLocally
                      ? "Show VIP tag"
                      : "Hide VIP tag"}
                </AdminActionButton>
              )}
            </AdminActionsContainer>
          )}

          {!isImageOutput(collection?.modelName) && (
            <TabsContainer $activeTab={selectedTab}>
              <Tab
                onClick={() => {
                  if (!isLoading) {
                    setSelectedTab(MODAL_CONTENT.IMAGE_TO_VIDEO);
                  }
                }}
                $selected={selectedTab === MODAL_CONTENT.IMAGE_TO_VIDEO}
              >
                <ImageIcon
                  color={
                    selectedTab === MODAL_CONTENT.IMAGE_TO_VIDEO
                      ? "#000"
                      : "#fff"
                  }
                />
                <TabText
                  $selected={selectedTab === MODAL_CONTENT.IMAGE_TO_VIDEO}
                >
                  Image to{" "}
                  {isImageOutput(collection?.modelName) ? "Image" : "Video"}
                </TabText>
              </Tab>
              <Tab
                $disabled={isImageOutput(collection?.modelName)}
                onClick={() => {
                  if (!isLoading) {
                    setSelectedTab(MODAL_CONTENT.EXTEND_VIDEO);
                  }
                }}
                $selected={selectedTab === MODAL_CONTENT.EXTEND_VIDEO}
              >
                <VideoRecorderIcon
                  color={
                    selectedTab === MODAL_CONTENT.EXTEND_VIDEO ? "#000" : "#fff"
                  }
                />
                <TabText $selected={selectedTab === MODAL_CONTENT.EXTEND_VIDEO}>
                  Extend Video
                </TabText>
              </Tab>
            </TabsContainer>
          )}

          <Content>
            <Container>
              {selectedTab === MODAL_CONTENT.EXTEND_VIDEO ? (
                <ExtendVideo
                  uploadedVideo={uploadedVideo}
                  setUploadedVideo={setUploadedVideo}
                  uploadedVideoRef={uploadedVideoRef}
                  onOpenGalleryModal={() => setIsUserGalleryModalOpen(true)}
                  galleryOnly
                  // handleCheckboxClick={handleCheckboxClick}
                  // agreeTos={agreeTos}
                  // showOutline={showOutline}
                  // shouldShake={shouldShake}
                  generateContainer={
                    <GenerateContainer>
                      {renderGenerateContainer()}
                    </GenerateContainer>
                  }
                />
              ) : (
                <ImageToVideo
                  uploadedImage={uploadedImage}
                  setUploadedImage={setUploadedImage}
                  setIsOpenCropModal={setIsOpenCropModal}
                  onOpenGalleryModal={() => setIsUserGalleryModalOpen(true)}
                  nudifyEnabled={nudifyEnabled}
                  setNudifyEnabled={setNudifyEnabled}
                  upscaleEnabled={upscaleEnabled}
                  setUpscaleEnabled={setUpscaleEnabled}
                  // handleCheckboxClick={handleCheckboxClick}
                  // agreeTos={agreeTos}
                  // showOutline={showOutline}
                  // shouldShake={shouldShake}
                  uploadedImageRef={uploadedImageRef}
                  generateContainer={
                    <GenerateContainer>
                      {renderGenerateContainer()}
                    </GenerateContainer>
                  }
                />
              )}
              <MemoizedRightContent
                output={collection?.output}
                input={collection?.input}
              />
            </Container>
          </Content>

          {/* {!isImageOutput(collection.modelName) && (
            <CheckboxContainerRow>
              {selectedTab !== MODAL_CONTENT.EXTEND_VIDEO && (
                <CheckboxContainer
                  onClick={() => {
                    setNudifyEnabled(!nudifyEnabled);
                  }}
                >
                  <LottieIcon
                    animationData={checkboxAnimation}
                    trigger="toggle-reverse"
                    isActive={nudifyEnabled}
                    speed={2}
                    onActiveChange={() => {
                      setNudifyEnabled(!nudifyEnabled);
                    }}
                    onClick={() => {
                      setNudifyEnabled(!nudifyEnabled);
                    }}
                    size={24}
                    strokeColor={"#2877d9"}
                  />
                  <NudifyText>Nudify</NudifyText>
                </CheckboxContainer>
              )}
              {selectedTab !== MODAL_CONTENT.EXTEND_VIDEO && (
                <CheckboxContainer
                  onClick={() => {
                    setUpscaleEnabled(!upscaleEnabled);
                  }}
                >
                  <LottieIcon
                    animationData={checkboxAnimation}
                    trigger="toggle-reverse"
                    isActive={upscaleEnabled}
                    speed={2}
                    onActiveChange={() => {
                      setUpscaleEnabled(!upscaleEnabled);
                    }}
                    onClick={() => {
                      setUpscaleEnabled(!upscaleEnabled);
                    }}
                    size={24}
                    strokeColor={"#2877d9"}
                  />
                  <NudifyText>Upscale</NudifyText>
                </CheckboxContainer>
              )}
              <CheckboxContainer
                $isExtendVideoTab={selectedTab === MODAL_CONTENT.EXTEND_VIDEO}
                onClick={handleAudioToggle}
              >
                <LottieIcon
                  animationData={checkboxAnimation}
                  trigger="toggle-reverse"
                  isActive={audioEnabled}
                  speed={2}
                  onActiveChange={handleAudioToggle}
                  onClick={handleAudioToggle}
                  size={24}
                  strokeColor={"#2877d9"}
                />
                <NudifyText>
                  Audio
                  <Badge>
                    <BetaIcon />
                  </Badge>
                </NudifyText>
              </CheckboxContainer>
            </CheckboxContainerRow>
          )} */}

          <GenerateContainerMobile>
            {renderGenerateContainer()}
          </GenerateContainerMobile>
          {/* <TermsCheckboxComponent
            mobile
            handleCheckboxClick={handleCheckboxClick}
            agreeTos={agreeTos}
            shouldShake={shouldShake}
            showOutline={showOutline}
            offMarginTop
          /> */}
        </ModalContainer>
      </Modal>
      {flyAnimation.isActive && (
        <FlyToCollection
          isActive={flyAnimation.isActive}
          imageUrl={flyAnimation.imageUrl}
          startPosition={flyAnimation.startPosition}
          imageDimensions={flyAnimation.imageDimensions}
          onComplete={() => {
            dispatch(triggerCollectionTabAnimation());

            // Call callback from ImageToVideo if provided
            if (flyAnimation.onComplete) {
              flyAnimation.onComplete();
            }

            setFlyAnimation({
              isActive: false,
              imageUrl: null,
              startPosition: null,
              imageDimensions: null,
              onComplete: null,
            });
          }}
        />
      )}
      <EnterPromptModal
        isOpen={isEnterPromptModalOpen}
        onClose={() => setIsEnterPromptModalOpen(false)}
        overlayZIndex={3700}
        customPrompt={customPrompt}
        setCustomPrompt={setCustomPrompt}
        negativePrompt={negativePrompt}
        setNegativePrompt={setNegativePrompt}
        audioPrompt={audioPrompt}
        setAudioPrompt={setAudioPrompt}
        audioEnabled={audioEnabled}
        setAudioEnabled={setAudioEnabled}
        videoResolution={videoResolution}
        setVideoResolution={setVideoResolution}
        enableVideoQuality={!isImageOutput(collection.modelName)}
        modelPrice={
          selectedTab === MODAL_CONTENT.IMAGE_TO_VIDEO
            ? collection?.price +
              (nudifyEnabled ? NUDIFY_PRICE : 0) +
              (upscaleEnabled ? UPSCALE_PRICE : 0)
            : collection.price
        }
        setCustomSeed={setCustomSeed}
        customSeed={customSeed}
        collectionSeed={collection?.seed}
        fps={fps}
        setFps={setFps}
        videoDuration={videoDuration}
        setVideoDuration={setVideoDuration}
        setShowPrompts={setShowPrompts}
        showPrompts={showPrompts}
        resetSeed={isOpen}
        isMinVideoLength8sec={collection?.output?.videoDuration === 8}
        collectionId={collection?._id}
      />
      <ConfirmationModal
        isOpen={!!confirmationAction}
        onClose={() => setConfirmationAction(null)}
        onConfirm={handleConfirmVisibilityAction}
        title={confirmationTitle}
        message={confirmationMessage}
        confirmText="Confirm"
        cancelText="Cancel"
        confirmButtonType="danger"
        isLoading={confirmationLoading}
      />
      <AddToPlaylistModal
        isOpen={isAddToPlaylistModalOpen}
        isCreatingNew={isCreatingNew}
        setIsCreatingNew={setIsCreatingNew}
        openCreatePlaylistModal={() => {
          setIsCreatePlaylistModalOpen(true);
        }}
        onClose={() => setIsAddToPlaylistModalOpen(false)}
        collectionId={collection?._id}
      />
      <CreatePlaylistModal
        playlistName={playlistName}
        setPlaylistName={setPlaylistName}
        isOpen={isCreatePlaylistModalOpen}
        onClose={() => setIsCreatePlaylistModalOpen(false)}
      />
    </>
  );
};

export default GenerateCollectionModal;
